import React from "react";
import GuidePanel from "../GuidePanel.js";
import {
  GuideLead,
  GuideCallout,
  GuideSectionLabel,
  GuideCard,
  GuideCardGrid,
  GuideCompare,
} from "../GuideBlocks.js";

const AssumptionsVsFactsSection = ({ heading, accentIndex = 3, getVisual, getAccent }) => (
  <GuidePanel
    sectionId="assumptions-vs-facts"
    heading={heading}
    accentIndex={accentIndex}
    getVisual={getVisual}
    getAccent={getAccent}
  >
    <GuideLead>
      &quot;They didn&apos;t reply, so they must be upset with me.&quot; Notice how fast that
      sentence moves from a fact to a conclusion. The fact is: they didn&apos;t reply. Everything
      after &quot;so&quot; is an assumption — and most of us respond to the assumption as if it
      were the fact.
    </GuideLead>
    <GuideSectionLabel>Why we blur the two</GuideSectionLabel>
    <p className="guide-panel__p">
      Your mind does not like unfinished stories. The moment something happens, it rushes to fill
      in the &quot;why&quot; — because ambiguity feels worse than a weak explanation. So it grabs
      the fastest available explanation and files it as true.
    </p>
    <p className="guide-panel__p">
      This is efficient. It is also how a lot of unnecessary conflict, pressure, and poor decisions
      get made.
    </p>
    <GuideSectionLabel>How to tell them apart</GuideSectionLabel>
    <GuideCompare
      leftTitle="Fact"
      left={
        <p>
          Something you could film with a camera. &quot;The email said let&apos;s discuss this
          later.&quot;
        </p>
      }
      rightTitle="Assumption"
      right={
        <p>
          The meaning you attached. &quot;They&apos;re annoyed with me&quot; is not on the
          recording — it is a story layered on top.
        </p>
      }
    />
    <GuideCallout>
      A simple test: could a stranger with no context confirm this by watching what happened? If
      not, it is an assumption.
    </GuideCallout>
    <GuideSectionLabel>Where this fits in EIER</GuideSectionLabel>
    <p className="guide-panel__p">
      This distinction is the heart of EIER — <strong>Event → Interpretation → Evidence →
      Response</strong>. The Event is the fact. The Interpretation is where assumption sneaks in
      disguised as observation. Evidence is where you check that story against what you can
      actually support.
    </p>
    <p className="guide-panel__p">
      Most reactive decisions do not come from the Event at all. They come from responding to an
      unexamined Interpretation as though it were the Event itself.
    </p>
    <GuideSectionLabel>Practicing the separation</GuideSectionLabel>
    <GuideCardGrid columns={2}>
      <GuideCard
        iconKey="clarity"
        title="Write two columns"
        description='"What happened" on one side, "what I am assuming it means" on the other. Seeing them apart breaks the fusion.'
      />
      <GuideCard
        iconKey="thinking"
        title="Ask for one other explanation"
        description="You do not have to believe it — generating an alternative proves the first one was not fact."
      />
      <GuideCard
        iconKey="communication"
        title="Get the missing information"
        description="Often the fastest way to close the gap between assumption and fact is simply to ask."
      />
      <GuideCard
        iconKey="awareness"
        title="Notice the certainty of your language"
        description='"I know they are mad" and "I am guessing they might be upset" describe the same event — only one is honest about what is assumption.'
      />
    </GuideCardGrid>
    <GuideCallout variant="amber">
      Better decisions start with a simple discipline: know what you actually know, separate from
      what you are afraid might be true.
    </GuideCallout>
  </GuidePanel>
);

export default AssumptionsVsFactsSection;
